(() => {
  document.documentElement.classList.add('coven-js-ready');
  const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

  function initMagic(root = document) {
    const stages = [];
    if (root.matches?.('.coven-section-stage')) stages.push(root);
    root.querySelectorAll?.('.coven-section-stage').forEach((el) => stages.push(el));

    stages.forEach((stage) => {
      if (stage.dataset.covenMagicReady === 'true') return;
      stage.dataset.covenMagicReady = 'true';

      const intensity = clamp(Number(stage.dataset.covenMagicIntensity || 0), 0, 10);
      if (intensity > 0) stage.classList.add('is-magic-active');

      const wallpaper = stage.querySelector('[data-coven-reactive-background]');
      const onMove = (event) => {
        if (!intensity) return;
        const rect = stage.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        const x = clamp((event.clientX - rect.left) / rect.width, 0, 1);
        const y = clamp((event.clientY - rect.top) / rect.height, 0, 1);
        stage.style.setProperty('--coven-pointer-x', `${(x * 100).toFixed(2)}%`);
        stage.style.setProperty('--coven-pointer-y', `${(y * 100).toFixed(2)}%`);
        if (wallpaper && window.innerWidth > 749) {
          const maxShift = intensity * 0.9;
          stage.style.setProperty('--coven-parallax-x', `${((x - .5) * maxShift).toFixed(2)}px`);
          stage.style.setProperty('--coven-parallax-y', `${((y - .5) * maxShift).toFixed(2)}px`);
        }
      };
      const onLeave = () => {
        stage.style.setProperty('--coven-pointer-x', '50%');
        stage.style.setProperty('--coven-pointer-y', '50%');
        stage.style.setProperty('--coven-parallax-x', '0px');
        stage.style.setProperty('--coven-parallax-y', '0px');
      };
      stage.addEventListener('pointermove', onMove, { passive: true });
      stage.addEventListener('pointerleave', onLeave, { passive: true });
    });
  }

  function initReveal(root = document) {
    const targets = [];
    if (root.matches?.('.coven-section-stage[data-coven-reveal]')) targets.push(root);
    root.querySelectorAll?.('.coven-section-stage[data-coven-reveal]').forEach((el) => targets.push(el));
    if (!targets.length) return;

    if (!('IntersectionObserver' in window)) {
      targets.forEach((el) => el.classList.add('is-coven-visible'));
      return;
    }

    const observer = new IntersectionObserver((entries, obs) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-coven-visible');
          obs.unobserve(entry.target);
        }
      });
    }, { threshold: .08, rootMargin: '0px 0px -6% 0px' });

    targets.forEach((el) => {
      if (el.dataset.covenReveal === 'none') el.classList.add('is-coven-visible');
      else observer.observe(el);
    });
  }

  function initNav(root = document) {
    const navs = [];
    if (root.matches?.('[data-coven-sticky-nav]')) navs.push(root);
    root.querySelectorAll?.('[data-coven-sticky-nav]').forEach((el) => navs.push(el));
    navs.forEach((nav) => {
      if (nav.dataset.covenNavReady === 'true') return;
      nav.dataset.covenNavReady = 'true';
      const toggle = nav.querySelector('[data-coven-nav-toggle]');
      const panel = nav.querySelector('[data-coven-nav-panel]');
      // Desktop dropdowns: geometry-based persistence.
      // Do not trust pointerleave alone here: Horizon can place page content in a competing
      // stacking layer beneath the header, which can steal pointer hit-testing as the cursor
      // crosses from a pill into its submenu. We keep one active menu open while the pointer
      // remains anywhere inside the combined trigger + submenu corridor.
      const navItems = Array.from(nav.querySelectorAll('.coven-sticky-nav__item'));
      let activeItem = null;
      let closeTimer = null;

      const clearCloseTimer = () => {
        if (closeTimer) {
          window.clearTimeout(closeTimer);
          closeTimer = null;
        }
      };

      const setExpanded = (item, expanded) => {
        if (!item) return;
        item.classList.toggle('is-open', expanded);
        const itemTrigger = item.querySelector('.coven-sticky-nav__trigger');
        if (itemTrigger) itemTrigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      };

      const closeActive = () => {
        clearCloseTimer();
        setExpanded(activeItem, false);
        activeItem = null;
      };

      const scheduleClose = (delay = 650) => {
        if (!activeItem || closeTimer) return;
        closeTimer = window.setTimeout(closeActive, delay);
      };

      const openItem = (item) => {
        if (!item?.querySelector('.coven-sticky-nav__dropdown')) return;
        clearCloseTimer();
        if (activeItem && activeItem !== item) setExpanded(activeItem, false);
        activeItem = item;
        setExpanded(item, true);
      };

      const pointerInsideActiveMenu = (x, y) => {
        if (!activeItem) return false;
        const trigger = activeItem.querySelector('.coven-sticky-nav__trigger');
        const dropdown = activeItem.querySelector('.coven-sticky-nav__dropdown');
        if (!trigger || !dropdown) return false;
        const t = trigger.getBoundingClientRect();
        const d = dropdown.getBoundingClientRect();
        const padX = 18;
        const padY = 16;
        // One padded rectangle joins the pill and submenu, including the diagonal path a
        // human cursor naturally takes into the menu.
        const left = Math.min(t.left, d.left) - padX;
        const right = Math.max(t.right, d.right) + padX;
        const top = Math.min(t.top, d.top) - padY;
        const bottom = Math.max(t.bottom, d.bottom) + padY;
        return x >= left && x <= right && y >= top && y <= bottom;
      };

      navItems.forEach((item) => {
        const dropdown = item.querySelector('.coven-sticky-nav__dropdown');
        const trigger = item.querySelector('.coven-sticky-nav__trigger');

        if (!dropdown || !trigger) {
          item.addEventListener('pointerenter', () => {
            if (window.innerWidth >= 900) closeActive();
          });
          return;
        }

        trigger.setAttribute('aria-haspopup', 'true');
        trigger.setAttribute('aria-expanded', 'false');

        item.addEventListener('pointerenter', () => {
          if (window.innerWidth >= 900) openItem(item);
        });
        trigger.addEventListener('pointerenter', () => {
          if (window.innerWidth >= 900) openItem(item);
        });
        dropdown.addEventListener('pointerenter', () => {
          if (window.innerWidth >= 900) openItem(item);
        });

        item.addEventListener('focusin', () => openItem(item));
        item.addEventListener('focusout', (event) => {
          if (!item.contains(event.relatedTarget)) scheduleClose(250);
        });
      });

      // Document-level geometry tracking means the submenu stays alive even if another
      // transparent Horizon layer happens to receive the pointer event during the crossing.
      document.addEventListener('pointermove', (event) => {
        if (!activeItem || window.innerWidth < 900) return;
        if (pointerInsideActiveMenu(event.clientX, event.clientY)) clearCloseTimer();
        else scheduleClose(650);
      }, { passive: true });

      document.addEventListener('pointerdown', (event) => {
        if (activeItem && !activeItem.contains(event.target)) closeActive();
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') closeActive();
      });

      if (toggle && panel) {
        toggle.addEventListener('click', () => {
          const open = panel.classList.toggle('is-open');
          toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        });
        document.addEventListener('click', (event) => {
          if (!nav.contains(event.target)) {
            panel.classList.remove('is-open');
            toggle.setAttribute('aria-expanded', 'false');
          }
        });
      }

      if (nav.classList.contains('is-sticky')) {
        const wrapper = nav.closest('.shopify-section') || nav.parentElement;
        const pageWrapper = document.querySelector('.page-wrapper');
        const usePageWrapper = () => !!pageWrapper && window.innerWidth >= 990;
        const scrollTop = () => usePageWrapper() ? pageWrapper.scrollTop : window.scrollY;
        let threshold = 0;
        const recalc = () => {
          const wasFixed = nav.classList.contains('is-fixed');
          if (wasFixed) nav.classList.remove('is-fixed');
          if (wrapper) wrapper.style.minHeight = '';
          const rect = nav.getBoundingClientRect();
          if (usePageWrapper()) {
            const rootRect = pageWrapper.getBoundingClientRect();
            threshold = pageWrapper.scrollTop + rect.top - rootRect.top;
          } else {
            threshold = window.scrollY + rect.top;
          }
          if (wasFixed) update();
        };
        const update = () => {
          const top = Number.parseFloat(getComputedStyle(nav).getPropertyValue('--nav-sticky-top')) || 0;
          const shouldFix = scrollTop() + top >= threshold;
          nav.classList.toggle('is-fixed', shouldFix);
          if (wrapper) wrapper.style.minHeight = shouldFix ? `${nav.offsetHeight}px` : '';
        };
        requestAnimationFrame(() => { recalc(); update(); });
        window.addEventListener('load', recalc, { once:true });
        window.addEventListener('scroll', update, { passive:true });
        pageWrapper?.addEventListener('scroll', update, { passive:true });
        window.addEventListener('resize', recalc, { passive:true });
        if ('ResizeObserver' in window) {
          const ro = new ResizeObserver(() => recalc());
          const headerGroup = document.querySelector('#header-group');
          if (headerGroup) ro.observe(headerGroup);
        }
      }
    });
  }

  function initGlobalMagic() {
    const body = document.body;
    if (!body || body.dataset.covenGlobalMagicReady === 'true') return;
    body.dataset.covenGlobalMagicReady = 'true';

    if (body.dataset.covenCursorMagic === 'true' && window.matchMedia('(pointer:fine)').matches) {
      const aura = document.createElement('div');
      aura.className = 'coven-cursor-aura';
      aura.setAttribute('aria-hidden', 'true');
      document.body.appendChild(aura);
      let targetX = -100, targetY = -100, x = -100, y = -100;
      window.addEventListener('pointermove', (e) => { targetX = e.clientX; targetY = e.clientY; }, { passive:true });
      document.addEventListener('pointerover', (e) => { aura.classList.toggle('is-hovering', !!e.target.closest('a,button,summary,.coven-category,.coven-product,.coven-flex-grid__item')); }, { passive:true });
      const animate = () => {
        x += (targetX - x) * .22; y += (targetY - y) * .22;
        aura.style.transform = `translate3d(${x - aura.offsetWidth/2}px,${y - aura.offsetHeight/2}px,0)`;
        requestAnimationFrame(animate);
      };
      requestAnimationFrame(animate);
    }

    if (body.dataset.covenClickSparkles === 'true') {
      document.addEventListener('click', (e) => {
        if (!e.target.closest('a,button,summary,.coven-category,.coven-product')) return;
        const vectors = [[-28,-18],[25,-22],[-22,23],[29,18]];
        vectors.forEach(([dx,dy],i) => {
          const s = document.createElement('span');
          s.className = 'coven-click-spark';
          s.textContent = i % 2 ? '✧' : '✦';
          s.style.left = `${e.clientX}px`; s.style.top = `${e.clientY}px`;
          s.style.setProperty('--spark-x', `${dx}px`); s.style.setProperty('--spark-y', `${dy}px`);
          document.body.appendChild(s);
          setTimeout(() => s.remove(), 700);
        });
      });
    }
  }

  function scan(root = document) {
    initGlobalMagic();
    initMagic(root);
    initReveal(root);
    initNav(root);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(), { once: true });
  else scan();

  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  document.addEventListener('shopify:section:reorder', () => scan());
})();
